import { rafflesEnum } from 'common/enums/AfterSales'
import { Alert, BottomSheet, Flex, formatToBRL, Tag, Text } from 'components/InterUI/InterUI'
import { convertYearMonthDayToString } from 'utils/commons'
import { Separator } from 'components/Separator/Separator'
import BannerWin from 'assets/png/details_banner_win.png'

import { IRaffle } from '../RafflesCarousel.types'
import * as S from './RaffleBottomSheet.styles'

interface RaffleBottomSheetProps {
  raffleDetails: IRaffle | null
  luckyNumber: string
  show: boolean
  onClose: () => void
}

type ITagThemes = 'yellow' | 'green' | 'red' | 'gray'

export const RaffleBottomSheet = ({
  show,
  luckyNumber,
  raffleDetails,
  onClose,
}: RaffleBottomSheetProps) => {
  const isStatusWin = raffleDetails?.status === rafflesEnum.WIN
  const isStatusRealized = raffleDetails?.status === rafflesEnum.REALIZED
  const showResultSection = isStatusRealized || isStatusWin
  const grayedOut = isStatusWin && !!raffleDetails?.paymentDate

  const renderTag = (text: string, theme: ITagThemes, tagColor?: boolean) => (
    <S.TagContent>
      <Tag
        size="small"
        {...(tagColor ? { color: 'var(--gray200)', colorText: 'var(--gray500)' } : { theme })}
      >
        {text}
      </Tag>
    </S.TagContent>
  )

  const statusTags: Record<rafflesEnum, JSX.Element> = {
    [rafflesEnum.WIN]: renderTag('Sorteio realizado', 'green', false),
    [rafflesEnum.NEXT]: renderTag('Próximo sorteio', 'yellow'),
    [rafflesEnum.SOON]: renderTag('Em breve', 'gray'),
    [rafflesEnum.REALIZED]: renderTag('Sorteio realizado', 'green', true),
  }

  const renderRaffleData = (
    <>
      <Flex
        justifyContent="space-between"
        direction="row"
        alignItems="center"
        style={{ marginTop: '16px' }}
      >
        <Text variant="body-3">Data sorteio</Text>
        <Text variant="body-3" as="h3" bold colorWeight={500}>
          {convertYearMonthDayToString(raffleDetails?.date ?? '')}
        </Text>
      </Flex>

      <Flex
        justifyContent="space-between"
        direction="row"
        alignItems="center"
        style={{ marginTop: '16px' }}
      >
        <Text variant="body-3">Seu número da sorte</Text>
        <Text variant="body-3" as="h3" bold colorWeight={500}>
          {luckyNumber}
        </Text>
      </Flex>

      {!isStatusWin && (
        <Flex
          justifyContent="space-between"
          direction="row"
          alignItems="center"
          style={{ marginTop: '16px' }}
        >
          <Text variant="body-3">Valor do prêmio</Text>

          <Text variant="body-3" as="h3" bold colorWeight={500}>
            {raffleDetails?.value ? formatToBRL(raffleDetails.value) : ''}
          </Text>
        </Flex>
      )}
    </>
  )

  const renderHeaderWin = isStatusWin && (
    <S.HeaderWin>
      <S.Banner $grayedOut={grayedOut}>
        <img src={BannerWin} alt="Banner Win" />
      </S.Banner>
    </S.HeaderWin>
  )

  const renderAlertWin = isStatusWin && !raffleDetails?.paymentDate && (
    <S.AlertContent>
      <Alert type="warning">Seu prêmio será depositado na sua conta em até 15 dias. </Alert>
    </S.AlertContent>
  )

  if (!show) {
    return null
  }

  return (
    <BottomSheet onClose={onClose}>
      <>
        {renderHeaderWin}

        <Flex
          justifyContent="space-between"
          direction="row"
          alignItems="center"
          style={{ marginBottom: '24px', minWidth: '200px' }}
        >
          <Flex direction="row" alignItems="center" style={{ minWidth: '100px' }}>
            <Text variant="body-3" as="h3" bold colorWeight={500}>
              {raffleDetails?.title}
            </Text>
          </Flex>
          <Flex direction="row" justifyContent="end" alignItems="flex-end" gap="16px">
            {raffleDetails?.extraChance && renderTag('Chance extra', 'green', grayedOut)}
            {statusTags[raffleDetails?.status as rafflesEnum]}
          </Flex>
        </Flex>

        {isStatusWin && <Separator borderWidth="1px" margin="0" type="dashed" />}

        {showResultSection && (
          <>
            <Flex justifyContent="space-between" direction="row" alignItems="center">
              <Text variant="body-3">Resultado</Text>

              <Text variant="body-3" as="h3" bold colorWeight={500}>
                {isStatusWin ? 'Você ganhou' : 'Não foi dessa vez'}
              </Text>
            </Flex>

            <Flex
              justifyContent="space-between"
              direction="row"
              alignItems="center"
              style={{ marginTop: '16px' }}
            >
              <Text variant="body-3">Número da sorte contemplado</Text>

              <Text variant="body-3" as="h3" bold colorWeight={500}>
                {raffleDetails?.winnerLuckyNumberOfTheMonth}
              </Text>
            </Flex>

            <Separator borderWidth="1px" margin="16px 0 0" type="dashed" />
          </>
        )}

        {renderRaffleData}

        {renderAlertWin}
      </>
    </BottomSheet>
  )
}
